interface Cond { field: string; op: string; value: string; conj: string }

const FIELD_LABELS: Record<string, string> = {
  name: "Name", title: "Title", company: "Company", industry: "Industry",
  location: "Location", skills: "Skills",
  status: "Email status", calling: "Calling status", linkedin: "LinkedIn status",
  campaign: "Campaign", aifit: "AI fit", tags: "Tags", contact: "Contact",
  notes: "Notes", history: "Previously contacted", cstatus: "Campaign status",
};
// All Candidates offers the full set; a wrapper may narrow it via
// data-advf-fields (the per-bot candidate tabs use contact/notes/history).
const DEFAULT_FIELDS = [
  "name", "title", "company", "industry", "location", "skills", "status", "calling",
  "linkedin", "campaign", "cstatus", "aifit", "tags", "contact",
];
const OPS: [string, string][] = [
  ["contains", "contains"], ["ncontains", "does not contain"], ["is", "is"],
  ["isnot", "is not"], ["empty", "is empty"], ["nempty", "is not empty"],
];
// Fields whose operators read better as plain statements, and which the server
// resolves as booleans / channel sets rather than text comparisons.
const OPS_BY_FIELD: Record<string, [string, string][]> = {
  contact: [
    ["contains", "has"], ["ncontains", "does not have"],
    ["empty", "has no contact"], ["nempty", "has any contact"],
  ],
  notes: [["yes", "has notes"], ["no", "has no notes"]],
  history: [["yes", "was previously contacted"], ["no", "was not previously contacted"]],
  skills: [
    ["contains", "contains all of"], ["ncontains", "contains none of"], ["any", "contains any of"],
    ["empty", "is empty"], ["nempty", "is not empty"],
  ],
};
const opsFor = (field: string): [string, string][] => OPS_BY_FIELD[field] || OPS;
// empty/nempty/yes/no are self-contained — no value control, and no reason to
// drop the condition for being valueless.
const VALUELESS_OPS = ["empty", "nempty", "yes", "no"];
const MAILIC = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 8l9 5 9-5"/></svg>';
const PHONEIC = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M5 4h4l2 5-3 2a12 12 0 005 5l2-3 5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z"/></svg>';
const LIIC = '<span class="li-mark">in</span>';

// Fields whose value is a fixed enum → a multi-select dropdown (pick one or more)
// rather than a free-text input. Values mirror the row data-f-* text.
const MULTI_FIELDS: Record<string, string[]> = {
  status: ["Hired", "Interview", "Not right now", "Interested", "Responded", "Clicked", "Opened", "Exhausted", "Auto responded", "Contacted"],
  // BotsProspect::STATUS_CALLING_LABELS, in the same order.
  calling: ["Interview booked", "Call back later", "Maybe later", "No answer", "Not interested", "Hired", "Disqualified", "Wrong person", "Not processed"],
  linkedin: ["Connected", "Request sent", "Not connected"],
  aifit: ["Strong", "Good", "Possible", "Bad", "None"],
};

const serverMultiFields = (raw: string | undefined): Record<string, string[]> => {
  try {
    const parsed = JSON.parse(raw || "{}");
    const fields: Record<string, string[]> = {};
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return fields;
    Object.keys(parsed).forEach((field) => {
      const list = parsed[field];
      if (FIELD_LABELS[field] && Array.isArray(list) && list.every((o: unknown) => typeof o === "string")) fields[field] = list;
    });
    return fields;
  } catch {
    return {};
  }
};

// re-applied on render_async_load so freshly loaded rows honour active conditions
let reapply: (() => void) | null = null;

export function initCandidatesAdvancedFilter(): void {
  const wrap = document.querySelector<HTMLElement>("[data-candidates-advf]");
  if (!wrap) { reapply = null; return; }
  if (wrap.dataset.advfBound === "1") { reapply?.(); return; } // already wired — rows may have (re)loaded
  wrap.dataset.advfBound = "1";

  const btn = wrap.querySelector<HTMLElement>("[data-advf-trigger]");
  const panel = wrap.querySelector<HTMLElement>("[data-advf-panel]");
  const rowsEl = wrap.querySelector<HTMLElement>("[data-advf-rows]");
  const emptyEl = wrap.querySelector<HTMLElement>("[data-advf-empty]");
  const addBtn = wrap.querySelector<HTMLElement>("[data-advf-add]");
  const cntEl = wrap.querySelector<HTMLElement>("[data-advf-count]");
  if (!btn || !panel || !rowsEl || !emptyEl || !addBtn || !cntEl) return;

  const multiFields: Record<string, string[]> = { ...MULTI_FIELDS, ...serverMultiFields(wrap.dataset.advfOptions) };
  const conds: Cond[] = [];
  const fieldKeys = (wrap.dataset.advfFields || "")
    .split(",").map((f) => f.trim()).filter((f) => FIELD_LABELS[f]);
  const FIELDS: [string, string][] =
    (fieldKeys.length ? fieldKeys : DEFAULT_FIELDS).map((f) => [f, FIELD_LABELS[f]]);
  const firstField = FIELDS[0][0];
  const blankCond = (): Cond => ({ field: firstField, op: opsFor(firstField)[0][0], value: "", conj: "and" });

  try {
    const parsed = JSON.parse(wrap.dataset.advfInitial || "[]");
    if (Array.isArray(parsed)) {
      parsed.forEach((c: any) => {
        if (c && typeof c.field === "string" && typeof c.op === "string" && FIELD_LABELS[c.field]) {
          conds.push({ field: c.field, op: c.op, value: String(c.value ?? ""), conj: c.conj === "or" ? "or" : "and" });
        }
      });
    }
  } catch { /* ignore malformed advf */ }
  const esc = (s: string): string => s.replace(/"/g, "&quot;");
  const opt = (list: [string, string][], val: string): string =>
    list.map(([v, l]) => `<option value="${v}"${v === val ? " selected" : ""}>${l}</option>`).join("");
  const needsVal = (op: string): boolean => !VALUELESS_OPS.includes(op);

  const chanCtrl = (c: Cond, i: number): string => {
    const on = (ch: string): string => ((c.value || "").split(",").indexOf(ch) !== -1 ? " active" : "");
    return `<div class="advf-chans" data-i="${i}">`
      + `<span class="advf-chan${on("email")}" data-ch="email" title="Email">${MAILIC}</span>`
      + `<span class="advf-chan${on("linkedin")}" data-ch="linkedin" title="LinkedIn">${LIIC}</span>`
      + `<span class="advf-chan${on("phone")}" data-ch="phone" title="Phone">${PHONEIC}</span></div>`;
  };
  const msLabel = (sel: string[]): string =>
    sel.length === 0 ? "Select…" : (sel.length <= 2 ? sel.join(", ") : `${sel.length} selected`);
  const msCtrl = (c: Cond, i: number): string => {
    const sel = (c.value || "").split(",").filter(Boolean);
    const menu = multiFields[c.field]
      .map((o) => `<label class="advf-ms-opt"><input type="checkbox" value="${esc(o)}"${sel.includes(o) ? " checked" : ""}>${o}</label>`)
      .join("");
    return `<div class="advf-ms advf-val" data-i="${i}"><button type="button" class="advf-ms-btn">${esc(msLabel(sel))}</button><div class="advf-ms-menu">${menu}</div></div>`;
  };
  const valCtrl = (c: Cond, i: number): string => {
    if (!needsVal(c.op)) return '<span class="advf-val" style="visibility:hidden"></span>';
    if (c.field === "contact") return chanCtrl(c, i);
    if (multiFields[c.field]) return msCtrl(c, i);
    const placeholder = c.field === "skills" ? "Skills, comma separated" : "Enter a value";
    return `<input class="advf-val" placeholder="${placeholder}" value="${esc(c.value || "")}">`;
  };

  const render = (): void => {
    emptyEl.style.display = conds.length ? "none" : "";
    rowsEl.innerHTML = conds.map((c, i) => {
      const conj = i === 0
        ? '<span class="advf-conj">Where</span>'
        : `<select class="advf-conj-sel"><option value="and"${c.conj === "and" ? " selected" : ""}>and</option><option value="or"${c.conj === "or" ? " selected" : ""}>or</option></select>`;
      return `<div class="advf-row" data-i="${i}">${conj}`
        + `<select class="advf-field">${opt(FIELDS, c.field)}</select>`
        + `<select class="advf-op">${opt(opsFor(c.field), c.op)}</select>`
        + valCtrl(c, i)
        + '<span class="advf-del" title="Remove"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 7v13a1 1 0 001 1h10a1 1 0 001-1V7"/></svg></span></div>';
    }).join("");
    const n = conds.length;
    if (n) { cntEl.textContent = String(n); cntEl.style.display = ""; } else cntEl.style.display = "none";
    btn.classList.toggle("has-val", n > 0);
  };

  const effective = (): Cond[] => conds.filter((c) => c.field && c.op && (!needsVal(c.op) || c.value));
  const filterForm = (): HTMLFormElement | null =>
    document.querySelector<HTMLFormElement>("form[data-candidates-filter-form]");

  let dirty = false;
  const apply = (): void => { dirty = true; };
  reapply = null; // server does the filtering — nothing to re-apply on (re)load

  const commit = (): void => {
    if (!dirty) return;
    dirty = false;
    const form = filterForm();
    if (!form) return;
    let advf = form.querySelector<HTMLInputElement>('input[name="advf"]');
    if (!advf) {
      advf = document.createElement("input");
      advf.type = "hidden";
      advf.name = "advf";
      form.appendChild(advf);
    }
    const kept = effective();
    const next = kept.length ? JSON.stringify(kept) : "";
    if (advf.value === next) return; // no effective change — don't reload / touch the URL
    advf.value = next;
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.submit();
  };

  addBtn.addEventListener("click", () => {
    conds.push(blankCond());
    render();
    apply();
  });

  rowsEl.addEventListener("input", (e) => {
    const t = e.target as HTMLElement;
    const rowEl = t.closest<HTMLElement>(".advf-row");
    if (!rowEl) return;
    const c = conds[Number(rowEl.dataset.i || "0")];
    if (!c) return;
    // multi-select (status / AI fit) checkbox toggled — update in place, keep the menu open
    const ms = t.closest<HTMLElement>(".advf-ms");
    if (ms && (t as HTMLInputElement).type === "checkbox") {
      const picked = Array.from(ms.querySelectorAll<HTMLInputElement>(".advf-ms-opt input:checked")).map((cb) => cb.value);
      c.value = picked.join(",");
      const b = ms.querySelector<HTMLElement>(".advf-ms-btn");
      if (b) b.textContent = msLabel(picked);
      apply();
      return;
    }
    if (t.classList.contains("advf-field")) {
      c.field = (t as HTMLSelectElement).value;
      c.op = opsFor(c.field)[0][0]; // the old op may not exist on the new field
      c.value = "";
      render();
    }
    else if (t.classList.contains("advf-op")) { c.op = (t as HTMLSelectElement).value; render(); }
    else if (t.classList.contains("advf-val")) c.value = (t as HTMLInputElement).value;
    else if (t.classList.contains("advf-conj-sel")) c.conj = (t as HTMLSelectElement).value;
    apply();
  });

  rowsEl.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    // toggle a multi-select (status / AI fit) dropdown open/closed
    const msBtn = t.closest<HTMLElement>(".advf-ms-btn");
    if (msBtn) {
      const ms = msBtn.closest<HTMLElement>(".advf-ms")!;
      const wasOpen = ms.classList.contains("open");
      wrap.querySelectorAll(".advf-ms.open").forEach((o) => o.classList.remove("open"));
      if (!wasOpen) ms.classList.add("open");
      return;
    }
    const chan = t.closest<HTMLElement>(".advf-chan");
    if (chan) {
      const c = conds[Number(chan.closest<HTMLElement>(".advf-row")!.dataset.i || "0")];
      const ch = chan.dataset.ch as string;
      const set = c.value ? c.value.split(",") : [];
      const ix = set.indexOf(ch);
      if (ix === -1) set.push(ch); else set.splice(ix, 1);
      c.value = set.join(",");
      chan.classList.toggle("active");
      apply();
      return;
    }
    const del = t.closest<HTMLElement>(".advf-del");
    if (!del) return;
    conds.splice(Number(del.closest<HTMLElement>(".advf-row")!.dataset.i || "0"), 1);
    render();
    apply();
  });

  // Enter inside a condition input must not submit the surrounding GET filter form
  panel.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Enter") e.preventDefault(); });

  const VIEWPORT_MARGIN = 8;
  const positionPanel = () => {
    panel.style.left = "0px";
    const overflow = panel.getBoundingClientRect().right - (window.innerWidth - VIEWPORT_MARGIN);
    if (overflow <= 0) return;

    const roomToTheLeft = Math.max(wrap.getBoundingClientRect().left - VIEWPORT_MARGIN, 0);
    panel.style.left = `${-Math.min(overflow, roomToTheLeft)}px`;
  };

  window.addEventListener("resize", () => {
    if (wrap.classList.contains("open")) positionPanel();
  });

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    const opening = !wrap.classList.contains("open");
    wrap.classList.toggle("open");
    if (opening && !conds.length) { conds.push(blankCond()); render(); }
    // measure only once the panel is displayed — it is display:none while closed
    if (opening) positionPanel();
    if (!opening) commit(); // toggled closed → apply server-side
  });
  panel.addEventListener("click", (e) => {
    e.stopPropagation();
    // clicking elsewhere in the panel closes any open multi-select menu
    if (!(e.target as HTMLElement).closest(".advf-ms")) {
      wrap.querySelectorAll(".advf-ms.open").forEach((o) => o.classList.remove("open"));
    }
  });
  document.addEventListener("click", () => {
    if (!wrap.classList.contains("open")) return;
    wrap.classList.remove("open");
    commit(); // closed by an outside click → apply server-side
  });

  // show any hydrated conditions (and the count badge) on first render
  if (conds.length) render();
}
